import { useMemo } from "react";
import { Link, useNavigation } from "@remix-run/react";
import { RemixLinkProps } from "@remix-run/react/dist/components";
import clsx from "clsx";

interface LinkProps extends Omit<RemixLinkProps, "children"> {
  children: React.ReactNode | ((isLoading: boolean) => React.ReactNode);
  size?: "SMALL" | "LARGE";
  isDisabled?: boolean;
}

export function PrimaryLink({ children, className, isDisabled, size, ...props }: LinkProps) {
  const isLoading = useIsLoading(props.to.toString());

  return (
    <Link
      className={clsx(
        size === "SMALL" ? "px-xs py-3xs text-sm" : "px-sm py-2xs text-base",
        "rounded border border-solid border-brand",
        "text-white",
        isDisabled || isLoading
          ? "pointer-events-none cursor-default border-disabled bg-disabled"
          : "bg-brand hover:bg-brand-light",
        className,
      )}
      data-component="PrimaryLink"
      {...props}
    >
      {typeof children === "function" ? children(isLoading) : children}
    </Link>
  );
}

export function SecondaryLink({ children, className, isDisabled, size, ...props }: LinkProps) {
  const isLoading = useIsLoading(props.to.toString());

  return (
    <Link
      className={clsx(
        size === "SMALL" ? "px-xs py-3xs text-sm" : "px-sm py-2xs text-base",
        "border border-solid",
        "rounded",
        isDisabled || isLoading
          ? "pointer-events-none cursor-default border-gray-300 bg-gray-200 text-gray-500"
          : "border-gray-300 bg-white text-black hover:bg-gray-100",
        className,
      )}
      data-component="SecondaryLink"
      {...props}
    >
      {typeof children === "function" ? children(isLoading) : children}
    </Link>
  );
}

// TODO: this doesn't work with edit because path is included in useEditLink

function useIsLoading(to: string) {
  const navigation = useNavigation();

  const isInTransition = navigation.state === "loading";

  const isMatchingLink = useMemo(() => {
    if (!navigation.location?.search) return false;

    let isMatching = true;
    const targetSearchParams = new URLSearchParams(to);
    const navSearchParams = new URLSearchParams(navigation.location.search);

    for (const [key, value] of targetSearchParams.entries()) {
      if (navSearchParams.getAll(key).includes(value)) continue;
      isMatching = false;
      break;
    }

    return isMatching;
  }, [to, navigation]);

  return isInTransition && isMatchingLink;
}
